import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import extractText from 'flarum/common/utils/extractText';

/**
 * BlueEmotion · site footer.
 *
 * Mounted by `js/src/forum/index.ts` into Flarum's existing
 * `<footer id="footer" class="App-footer">` extension slot via `m.mount()`.
 *
 * This component is intentionally static (no internal state). It's a Mithril
 * component rather than inline HTML so:
 *   - it lives in `components/` per Flarum convention,
 *   - other extensions can `extend()` / `override()` the view,
 *   - and we can add reactive bits later (live status, locale switcher, etc.)
 *     without rewriting the mount plumbing.
 *
 * All copy is translated via the `glowingblue-demo.forum.footer.*` keys
 * (resources/locale/en.yml). Brand names (BlueEmotion, Glowing Blue AG) stay
 * literal.
 */

export default class Footer extends Component {
  view(vnode) {
    const year = new Date().getFullYear();
    const baseUrl = (app.forum.attribute('baseUrl') || '').replace(/\/$/, '');
    const communityLinks = [{
      href: "".concat(baseUrl, "/tags"),
      label: app.translator.trans("glowingblue-demo.forum.footer.community.all_topics")
    }, {
      href: "".concat(baseUrl, "/polls"),
      label: app.translator.trans("glowingblue-demo.forum.footer.community.polls")
    }];

    // /badges only exists while fof/badges is enabled.
    if ('fof-badges' in flarum.extensions) {
      communityLinks.push({
        href: "".concat(baseUrl, "/badges"),
        label: app.translator.trans("glowingblue-demo.forum.footer.community.badges")
      });
    }
    return m("div", {
      className: "App-footer-inner container"
    }, m("div", {
      className: "App-footer-row App-footer-row--primary"
    }, this.brandBlock(baseUrl), m("nav", {
      className: "App-footer-links",
      "aria-label": extractText(app.translator.trans("glowingblue-demo.forum.footer.links_aria"))
    }, this.linksGroup(app.translator.trans("glowingblue-demo.forum.footer.community.heading"), communityLinks), this.linksGroup(app.translator.trans("glowingblue-demo.forum.footer.resources.heading"), [{
      href: "".concat(baseUrl, "/p/code-of-conduct"),
      label: app.translator.trans("glowingblue-demo.forum.footer.resources.code_of_conduct")
    }, {
      href: "".concat(baseUrl, "/p/privacy"),
      label: app.translator.trans("glowingblue-demo.forum.footer.resources.privacy")
    }, {
      href: "".concat(baseUrl, "/p/imprint"),
      label: app.translator.trans("glowingblue-demo.forum.footer.resources.imprint")
    }]))), m("div", {
      className: "App-footer-row App-footer-row--meta"
    }, m("div", {
      className: "App-footer-meta-left"
    }, m("span", {
      className: "App-footer-copy"
    }, app.translator.trans("glowingblue-demo.forum.footer.copyright", {
      year
    })))));
  }

  /**
   * Brand block — wordmark with the breathing Glow Blue mark glyph (styling
   * lives in less/forum/Footer.less), plus the sub-brand attribution.
   */
  brandBlock(baseUrl) {
    return m("div", {
      className: "App-footer-brand"
    }, m("a", {
      className: "App-footer-mark",
      href: "".concat(baseUrl, "/"),
      "aria-label": extractText(app.translator.trans("glowingblue-demo.forum.footer.home_aria"))
    }, m("span", {
      className: "App-footer-mark-glyph",
      "aria-hidden": "true"
    }), m("span", {
      className: "App-footer-mark-text"
    }, "BlueEmotion")), m("p", {
      className: "App-footer-attr"
    }, app.translator.trans("glowingblue-demo.forum.footer.attribution", {
      company: m("a", {
        href: "https://glowingblue.com",
        target: "_blank",
        rel: "noopener"
      }, "Glowing Blue AG")
    })));
  }

  /**
   * Render one labeled column of footer links.
   * Externalized so adding/swapping a group is one array literal away.
   */
  linksGroup(title, items) {
    return m("div", {
      className: "App-footer-links-group"
    }, m("h6", null, title), items.map(it => {
      const externalAttrs = it.external ? {
        target: '_blank',
        rel: 'noopener'
      } : {};
      return m("a", Object.assign({
        href: it.href
      }, externalAttrs), it.label, it.external ? m("i", {
        className: "fas fa-arrow-up-right-from-square",
        "aria-hidden": "true"
      }) : null);
    }));
  }
}
flarum.reg.add('glowingblue-demo', 'forum/components/Footer', Footer);